import { useQuery } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface AuditLogEntry {
  id: string
  timestamp: string
  actor_user_id: string
  actor_name: string | null
  actor_email: string | null
  target_user_id: string | null
  target_user_name: string | null
  target_user_email: string | null
  target_tenant_id: string | null
  target_tenant_name: string | null
  action: string
  action_display: string
  feature_name: string | null
  details: Record<string, any> | null
}

export interface AuditAction {
  key: string
  name: string
  description: string
}

interface AuditLogListResponse {
  entries: AuditLogEntry[]
  total: number
  limit: number
  offset: number
}

interface UseAuditLogOptions {
  actor_user_id?: string
  target_user_id?: string
  target_tenant_id?: string
  action?: string
  from_date?: string
  to_date?: string
  limit?: number
  offset?: number
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  auditLog: (options: UseAuditLogOptions) => ['audit-log', options],
  auditActions: ['audit-log-actions'],
  auditEntry: (entryId: string) => ['audit-log-entry', entryId],
}

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetch audit log entries with filters
 */
export function useAuditLog(options: UseAuditLogOptions = {}) {
  const {
    actor_user_id,
    target_user_id,
    target_tenant_id,
    action,
    from_date,
    to_date,
    limit = 50,
    offset = 0,
  } = options

  return useQuery<AuditLogListResponse>({
    queryKey: QUERY_KEYS.auditLog(options),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (actor_user_id) params.append('actor_user_id', actor_user_id)
      if (target_user_id) params.append('target_user_id', target_user_id)
      if (target_tenant_id) params.append('target_tenant_id', target_tenant_id)
      if (action) params.append('action', action)
      if (from_date) params.append('from_date', from_date)
      if (to_date) params.append('to_date', to_date)
      params.append('limit', String(limit))
      params.append('offset', String(offset))

      const res = await api.get(`/admin/audit-log?${params.toString()}`)
      return res.data
    },
  })
}

/**
 * Fetch available audit actions for filtering
 */
export function useAuditActions() {
  return useQuery<AuditAction[]>({
    queryKey: QUERY_KEYS.auditActions,
    queryFn: async () => {
      const res = await api.get('/admin/audit-log/actions')
      return res.data
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - actions don't change
  })
}

/**
 * Fetch single audit log entry
 */
export function useAuditLogEntry(entryId: string | null) {
  return useQuery<AuditLogEntry>({
    queryKey: QUERY_KEYS.auditEntry(entryId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/audit-log/${entryId}`)
      return res.data
    },
    enabled: !!entryId,
  })
}
